// owner: marketing-domain agent
import { z } from 'zod';
import { UlidSchema, UrlSchema } from './common';

const UtmToken = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9._~\-+ %]+$/, 'letters, digits and . _ ~ - + only');

export const UtmLinkCreateSchema = z.object({
  // B4: short links are an org-attributable feature, not an anonymous redirector. Every link must
  // belong to an org whose marketer-capable role the caller holds (checked in the route handler).
  orgId: UlidSchema,
  campaignId: UlidSchema.optional(),
  destination: UrlSchema.max(2048),
  source: UtmToken,
  medium: UtmToken,
  campaign: UtmToken,
  term: UtmToken.optional(),
  content: UtmToken.optional(),
});
export const UtmLinkSchema = z.object({
  id: UlidSchema,
  code: z.string(),
  shortUrl: z.string(),
  destination: z.string(),
  taggedUrl: z.string(),
  source: z.string(),
  medium: z.string(),
  campaign: z.string(),
  term: z.string().nullable(),
  content: z.string().nullable(),
  orgId: z.string().nullable(),
  campaignId: z.string().nullable(),
  clicks: z.number().int(),
  createdAt: z.number().int(),
});
export const UtmLinkListSchema = z.object({ items: z.array(UtmLinkSchema) });
export const UtmLinkListQuerySchema = z.object({ orgId: UlidSchema.optional() });
export const ShortCodeParamsSchema = z.object({ code: z.string().regex(/^[A-Za-z0-9]{6,12}$/) });

export const DayRangeQuerySchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

export const MarketingReportQuerySchema = DayRangeQuerySchema.extend({
  orgId: UlidSchema.optional(),
  groupBy: z.enum(['source', 'medium', 'campaign']).default('source'),
  format: z.enum(['json', 'csv']).default('json'),
});
export const MarketingReportRowSchema = z.object({
  key: z.string(),
  clicks: z.number().int(),
  conversions: z.number().int(),
  conversionRate: z.number().nullable(),
  attributedValueCents: z.number().int(),
  spendCents: z.number().int().nullable(),
  roas: z.number().nullable(),
});
export const MarketingReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  groupBy: z.enum(['source', 'medium', 'campaign']),
  rows: z.array(MarketingReportRowSchema),
});

// B11: 'signup'/'apply'/'follow'/'hire' are real events recorded server-side (see
// apps/api/src/marketing/attribution.ts `attributePendingConversions` / the follow-conversion
// helper) and can no longer be self-reported over this endpoint (the route handler rejects them
// with 400). Only 'custom' is accepted from the client, capped at
// CUSTOM_CONVERSION_VALUE_CAP_CENTS and deduped once per (user, link/campaign, kind) by a DB
// unique index (0940 migration) so repeated posts cannot inflate attributed value.
export const CUSTOM_CONVERSION_VALUE_CAP_CENTS = 100_000;
export const ConversionBodySchema = z.object({
  kind: z.enum(['signup', 'apply', 'follow', 'hire', 'custom']).default('custom'),
  valueCents: z.number().int().min(0).max(CUSTOM_CONVERSION_VALUE_CAP_CENTS).default(0),
  conversionId: z.string().min(1).max(128).optional(),
});
export const ConversionResultSchema = z.object({
  attributed: z.boolean(),
  utmLinkId: z.string().nullable(),
  campaignId: z.string().nullable(),
});

export const ContentPerformanceQuerySchema = DayRangeQuerySchema.extend({
  orgId: UlidSchema.optional(),
});
export const ContentPerformanceSchema = z.object({
  from: z.string(),
  to: z.string(),
  items: z.array(
    z.object({
      postId: UlidSchema,
      createdAt: z.number().int(),
      excerpt: z.string(),
      impressions: z.number().int(),
      reactions: z.number().int(),
      comments: z.number().int(),
      engagementRate: z.number().nullable(),
      seoEnabled: z.boolean(),
      geoEnabled: z.boolean(),
    }),
  ),
  totals: z.object({
    impressions: z.number().int(),
    reactions: z.number().int(),
    comments: z.number().int(),
    engagementRate: z.number().nullable(),
  }),
});

export const K_ANONYMITY_MIN = 5;
export const AudienceInsightsQuerySchema = DayRangeQuerySchema.extend({
  orgId: UlidSchema.optional(),
});
const BucketSchema = z.object({ label: z.string(), count: z.number().int() });
export const AudienceInsightsSchema = z.object({
  from: z.string(),
  to: z.string(),
  k: z.number().int(),
  /** false when fewer than k distinct viewers exist: no breakdown is disclosed at all. */
  disclosed: z.boolean(),
  audienceSize: z.number().int().nullable(),
  locations: z.array(BucketSchema),
  skills: z.array(BucketSchema),
});
